<script setup>
import DOMPurify from 'dompurify';
import { AlertCircle, Bot, MapPin, MessageSquareText, Send, Sparkles, ThumbsUp, ThumbsDown, X } from 'lucide-vue-next';
import { marked } from 'marked';

const emits = defineEmits(['ss:toggleChatBot']);

const cosmo = useCosmo();
const globalStore = globalConfig();
const messages = computed(() => cosmo?.conversation);
const inputMessage = ref('');
const textBox = ref(null);
const isTyping = ref(false);
const error = ref('');
const messagesContainer = ref(null);
const showChatBot = computed(() => cosmo.isOpen);
const suggestions = ref(null);
const geoPermissionState = ref('prompt');
const isRequestingGeo = ref(false);
const geoPromptExpanded = ref(true);
let geoPermissionStatus = null;

const shouldShowGeoPrompt = computed(() => geoPermissionState.value !== 'granted');
const geoPromptText = computed(() => {
  if (geoPermissionState.value === 'unsupported') {
    return 'Your browser does not support geolocation. We will do our best without precise location details.';
  }

  if (geoPermissionState.value === 'blocked') {
    return 'Location access is blocked. Enable location in your browser site settings for better parking recommendations.';
  }

  if (geoPermissionState.value === 'denied') {
    return 'Precise location improves parking recommendations. You can enable location access at any time.';
  }

  return 'Share your precise location to help the assistant give better parking recommendations.';
});

const renderMarkdown = (content) => DOMPurify.sanitize(marked.parse(String(content ?? ''), {
  breaks: true,
  gfm: true
}));

const requestGeolocationAccess = async (isUserRetry = false) => {
  if (typeof window === 'undefined' || !window.navigator?.geolocation || isRequestingGeo.value) return null;

  isRequestingGeo.value = true;

  return new Promise((resolve) => {
    window.navigator.geolocation.getCurrentPosition(
      (position) => {
        globalStore.setGeo({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: position.timestamp
        });
        globalStore.clearLocationError();
        geoPermissionState.value = 'granted';
        isRequestingGeo.value = false;
        resolve('granted');
      },
      (geoError) => {
        geoPermissionState.value = geoError?.code === 1
          ? (isUserRetry ? 'blocked' : 'denied')
          : 'prompt';
        isRequestingGeo.value = false;
        resolve(geoPermissionState.value);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

const syncGeoPermissionState = async () => {
  if (typeof window === 'undefined' || !window.navigator?.geolocation) {
    geoPermissionState.value = 'unsupported';
    return;
  }

  if (!window.navigator.permissions?.query) {
    geoPermissionState.value = globalStore.geo?.loc ? 'granted' : 'prompt';
    return;
  }

  try {
    const permissionStatus = await window.navigator.permissions.query({ name: 'geolocation' });
    if (!geoPermissionStatus) {
      geoPermissionStatus = permissionStatus;
      geoPermissionStatus.onchange = () => {
        geoPermissionState.value = geoPermissionStatus.state;
        if (geoPermissionStatus.state === 'granted' && !globalStore.geo?.loc) {
          requestGeolocationAccess();
        }
      };
    }
    if (permissionStatus.state !== 'denied' || geoPermissionState.value !== 'blocked') {
      geoPermissionState.value = permissionStatus.state;
    }
  } catch (err) {
    geoPermissionState.value = globalStore.geo?.loc ? 'granted' : 'prompt';
  }
};

const handleCompactGeoAction = async () => {
  const state = await requestGeolocationAccess(true);
  if (state && state !== 'granted') geoPromptExpanded.value = true;
};

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
}

const scrollToBottom = async () => {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
}

const addMessage = (content, role = 'user') => {
  const message = {
    id: content.id || crypto.randomUUID(),
    content: content.content || content,
    role,
    timestamp: Date.now(),
    ...(content.vote && { vote: content.vote })
  };

  messages?.value?.push(message);
  cosmo.setConversation(messages.value);
  scrollToBottom();
  return message;
}

const simulateTyping = (stop) => {
  if (!stop) {
    isTyping.value = true;
    return scrollToBottom();
  }
  isTyping.value = false;
}

const sendMessage = async () => {
  if (!inputMessage.value.trim() || isTyping.value) return;

  const userMessage = inputMessage.value.trim();
  inputMessage.value = '';
  error.value = '';

  textBox.value.focus();

  addMessage(userMessage, 'user')
  simulateTyping();
  const location = globalStore.geo?.loc ? globalStore.geo : globalStore.location;
  const { sessionId, message } = await useChatbot(
    userMessage,
    cosmo?.sessionId,
    Object.keys(location).length ? location : null
  );
  simulateTyping(true);
  cosmo.setSessionId(sessionId);

  try {
    addMessage(message, 'assistant');
    nextTick(() => {
      textBox.value.focus();
    });
  } catch (err) {
    error.value = err.message;
    isTyping.value = false;
  }
}

const clearError = () => {
  error.value = '';
}

const toggleChatBot = () => {
  cosmo.toggleChatbot();
  if (cosmo.isOpen) {
    nextTick(() => {
      textBox.value?.focus();
      scrollToBottom();
    });
  }
};

const vote = async (messageId, vote) => {
  const { success } = await useChatbotVote(messageId, vote);
  success && cosmo.setMessageVote(messageId, vote);
}

watch(geoPermissionState, (state) => {
  geoPromptExpanded.value = state !== 'denied' && state !== 'blocked';
}, { immediate: true });

watch(showChatBot, async (isOpen) => {
  if (!isOpen) return;

  await syncGeoPermissionState();
  if (geoPermissionState.value === 'prompt' || (geoPermissionState.value === 'granted' && !globalStore.geo?.loc)) {
    await requestGeolocationAccess();
  }
});

onMounted(async () => {
  suggestions.value = await useChatbotSuggestions();
  await syncGeoPermissionState();
  if (cosmo?.conversation?.length > 0) {
    scrollToBottom();
  }
});

onUnmounted(() => {
  if (geoPermissionStatus) {
    geoPermissionStatus.onchange = null;
    geoPermissionStatus = null;
  }
});
</script>

<template>
  <button
    class="fixed border-2 border-white z-[9998] right-2 bottom-2 bg-blue-500 text-white hover:bg-blue-600 transition-all delay-100 rounded-full p-4 shadow-xl"
    variant="link"
    @click="toggleChatBot"
    v-if="!showChatBot"
  >
    <div class="flex items-center space-x-2 text-white">
      <MessageSquareText
        class="h-6 w-6"
        v-if="!showChatBot"
      />
      <span class="hidden md:block">AI Assistant</span>
    </div>
  </button>
  <div class="fixed z-[2] w-full sm:w-[400px] sm:bottom-6 sm:right-0 top-0 h-0" :class="[showChatBot ? 'h-full z-[999999]' : '']">
    <div class="flex flex-col justify-between max-w-4xl mx-auto bg-gray-50" :class="[showChatBot ? 'h-full shadow-lg' : '']" v-if="showChatBot">
      <!-- Header -->
      <div class="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 rounded-full flex items-center justify-center">
            <NuxtImg class="h-full w-full" src="/images/spotsurferLogo.png" alt="Logo for Spotsurfer" />
          </div>
          <div>
            <h1 class="text-xl font-semibold text-gray-900">AI Assistant</h1>
            <p class="text-sm text-gray-500">Always here to help</p>
            <Button class="absolute right-2 top-2 z-[999999] bg-white hover:bg-gray-100" @click="toggleChatBot">
              <X class="w-8 h-8 fill-amber-600 text-black" />
            </Button>
          </div>
        </div>
      </div>

      <!-- Messages Container -->
      <div class="relative flex-1 overflow-y-auto px-6 py-4 space-y-4 h-full" ref="messagesContainer">
        <div v-if="shouldShowGeoPrompt">
          <button
            v-if="(geoPermissionState === 'denied' || geoPermissionState === 'blocked') && !geoPromptExpanded"
            class="ml-auto flex items-center gap-2 rounded-full border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
            :disabled="isRequestingGeo"
            @click="handleCompactGeoAction"
          >
            <MapPin class="h-3.5 w-3.5" />
            {{ isRequestingGeo ? 'Requesting...' : geoPermissionState === 'blocked' ? 'Location blocked' : 'Enable location' }}
          </button>

          <div v-else class="rounded-lg border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            <div class="flex items-start justify-between gap-2">
              <p>{{ geoPromptText }}</p>
              <button
                v-if="geoPermissionState === 'denied' || geoPermissionState === 'blocked'"
                class="text-xs font-medium text-blue-600 hover:text-blue-800"
                @click="geoPromptExpanded = false"
              >
                Hide
              </button>
            </div>

            <Button
              v-if="geoPermissionState !== 'unsupported' && geoPermissionState !== 'blocked'"
              class="mt-2 bg-blue-600"
              size="sm"
              :disabled="isRequestingGeo"
              @click="requestGeolocationAccess(true)"
            >
              {{ isRequestingGeo ? 'Requesting location...' : 'Enable location' }}
            </Button>

            <div v-if="geoPermissionState === 'blocked'" class="mt-2 space-y-2 text-xs">
              <p>Enable Location in your browser's site settings.</p>
              <p class="font-medium">Reload this page after changing the setting.</p>
            </div>
          </div>
        </div>

        <div v-if="cosmo?.conversation?.length === 0" class="text-center py-12">
          <Bot class="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">Start a conversation</h3>
          <p class="text-gray-500 text-sm">Ask me anything and I'll do my best to help!</p>
        </div>

        <div v-if="cosmo.conversation?.length < 1">
          <p class="flex items-center justify-end border-b text-sm text-blue-600 mb-4 pb-2">
            <Sparkles class="h-4 w-4 mr-1 stroke-blue-600"/>
            Suggested questions.
          </p>
          <ul class="flex flex-col gap-2 items-end" v-if="suggestions?.length > 0">
            <li
              class="text-blue-600 border border-blue-600 rounded-full bg-blue-100 px-2 py-1 cursor-pointer"
              v-for="{ content } in suggestions"
              @click="() => {
                inputMessage = content;
                sendMessage();
              }"
            >
              {{ content }}
            </li>
          </ul>
        </div>
        <div
          v-for="message in cosmo.conversation"
          :key="message.id"
          :class="[
            'flex',
            message.role === 'user' ? 'justify-end' : 'justify-start'
          ]"
        >
          <div
            :class="[
              'max-w-xs lg:max-w-md px-4 py-2 rounded-lg',
              message.role === 'user'
                ? 'bg-blue-500 text-white rounded-br-none'
                : 'bg-white text-gray-900 border border-gray-200 rounded-bl-none shadow-sm'
            ]"
          >
            <div v-if="message.role === 'user'" class="whitespace-pre-wrap">{{ message.content }}</div>
            <div v-else class="chatbot-markdown" v-html="renderMarkdown(message.content)" />
            <div v-if="message.role === 'user'" class="text-xs text-blue-100 mt-1">
              {{ formatTime(message.timestamp) }}
            </div>
            <div v-else class="flex justify-between items-center text-xs text-gray-400 mt-1">
              {{ formatTime(message.timestamp) }}
              <div class="flex gap-2">
                <ThumbsUp
                  class="cursor-pointer hover:fill-blue-400 hover:stroke-blue-600 transition-colors duration-200"
                  :class="{'stroke-blue-600 fill-blue-400' : message.vote === 'UPVOTE'}"
                  @click="vote(message.id, 'UPVOTE')"
                />
                <ThumbsDown
                  class="cursor-pointer hover:fill-red-400 hover:stroke-red-600 transition-colors duration-200"
                  :class="{'stroke-red-600 fill-red-400' : message.vote === 'DOWNVOTE'}"
                  @click="vote(message.id, 'DOWNVOTE')"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Typing indicator -->
        <div v-if="isTyping" class="flex justify-start">
          <div class="bg-white text-gray-900 border border-gray-200 rounded-lg rounded-bl-none shadow-sm px-4 py-2">
            <div class="flex space-x-1">
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.1s"></div>
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="px-6 py-2">
        <div class="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <AlertCircle class="w-5 h-5 text-red-500" />
            <span class="text-red-700">{{ error }}</span>
          </div>
          <button
            @click="clearError"
            class="text-red-600 hover:text-red-800 font-medium text-sm"
          >
            Dismiss
          </button>
        </div>
      </div>

      <!-- Input Form -->
      <div class="bg-white border-t border-gray-200 px-6 py-4">
        <form @submit.prevent="sendMessage" class="flex space-x-4">
          <div class="flex-1 relative">
            <input
              ref="textBox"
              v-model="inputMessage"
              type="text"
              placeholder="Type your message..."
              :disabled="isTyping"
              class="text-sm w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              @keydown.enter.prevent="sendMessage"
            />
          </div>
          <button
            type="submit"
            :disabled="!inputMessage.trim() || isTyping"
            class="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
          >
            <Send class="w-4 h-4" />
            <span class="hidden sm:inline">Send</span>
          </button>
        </form>

        <!-- <button
          class="absolute text-xs"
          variant="link"
          @click="cosmo.reset()"
          v-if="cosmo.conversation.length >= 2"
        >
          Clear Conversation
        </button> -->
      </div>
    </div>
  </div>
</template>

<style scoped>
.chatbot-markdown :deep(p:not(:last-child)),
.chatbot-markdown :deep(ul),
.chatbot-markdown :deep(ol),
.chatbot-markdown :deep(blockquote),
.chatbot-markdown :deep(pre) {
  margin-bottom: 0.5rem;
}

.chatbot-markdown :deep(ul),
.chatbot-markdown :deep(ol) {
  padding-left: 1.25rem;
}

.chatbot-markdown :deep(ul) {
  list-style: disc;
}

.chatbot-markdown :deep(ol) {
  list-style: decimal;
}

.chatbot-markdown :deep(h1),
.chatbot-markdown :deep(h2),
.chatbot-markdown :deep(h3) {
  margin-bottom: 0.375rem;
  font-weight: 600;
  line-height: 1.25;
}

.chatbot-markdown :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}

.chatbot-markdown :deep(blockquote) {
  border-left: 3px solid #d1d5db;
  padding-left: 0.75rem;
  color: #4b5563;
}

.chatbot-markdown :deep(code) {
  border-radius: 0.25rem;
  background: #f3f4f6;
  padding: 0.125rem 0.25rem;
  font-size: 0.875em;
}

.chatbot-markdown :deep(pre) {
  overflow-x: auto;
  border-radius: 0.375rem;
  background: #111827;
  padding: 0.75rem;
  color: #f9fafb;
}

.chatbot-markdown :deep(pre code) {
  background: transparent;
  padding: 0;
  color: inherit;
}
</style>
